import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Phone,
  MessageSquareText,
  Video,
  Mail,
  Users,
  Eye,
  EyeOff,
  Download,
  Printer,
  Sparkles,
  Lock,
  ArrowLeft,
  ShieldCheck,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const METRICS = {
  employees: 32,
  connections: 57,
  phone: 23,
  text: 14,
  video: 11,
  message: 9,
  shared: 0,
};

const MONTHLY = [
  { month: "Month 1", employees: 8, connections: 13 },
  { month: "Month 2", employees: 11, connections: 19 },
  { month: "Month 3", employees: 13, connections: 25 },
];

const CAN_SEE = [
  "Number of employees supported",
  "Total support connections",
  "General service utilization",
  "Usage trends over time",
  "Approved aggregate program information",
];

const CANNOT_SEE = [
  "Employee names",
  "Employee contact information",
  "Who contacted a chaplain",
  "Individual support reasons",
  "Messages or conversations",
  "Chaplain notes",
  "Private employee details",
];

function csvEscape(v) {
  const s = String(v);
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function downloadCsv() {
  const rows = [
    [
      "Reporting Period",
      "Organization",
      "Unique Employees Supported",
      "Total Support Connections",
      "Phone",
      "Text",
      "Video",
      "Message",
      "Private Employee Details Shared",
    ],
    [
      "Demo Quarter",
      "BALM",
      METRICS.employees,
      METRICS.connections,
      METRICS.phone,
      METRICS.text,
      METRICS.video,
      METRICS.message,
      METRICS.shared,
    ],
  ];
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "CallAChap_BALM_Demo_Utilization_Report.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function MetricTile({ value, label, sub, icon: Icon, testId, big }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl bg-white border border-slate-200 shadow-sm ${
        big ? "p-8 md:p-10" : "p-5 md:p-6"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && (
          <span
            className={`inline-flex items-center justify-center rounded-lg ${
              big ? "w-10 h-10 bg-rose-50 text-rose-500" : "w-8 h-8 bg-[#F0F4F8] text-[#4A6FA5]"
            }`}
          >
            <Icon className={big ? "w-5 h-5" : "w-4 h-4"} strokeWidth={2.2} aria-hidden="true" />
          </span>
        )}
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
          {label}
        </p>
      </div>
      <div
        className={`font-heading tracking-tight text-slate-900 ${
          big ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"
        }`}
      >
        {value}
      </div>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function EmployerDashboardDemo() {
  useEffect(() => {
    document.title = "CALLACHAP Employer Dashboard — BALM (Demonstration)";
  }, []);

  const methodTotal = useMemo(
    () => METRICS.phone + METRICS.text + METRICS.video + METRICS.message,
    []
  );

  return (
    <main data-testid="employer-dashboard-demo" className="bg-[#FAFBFD] text-slate-900 min-h-screen">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; }
          [data-testid=employer-dashboard-demo] { background: white !important; }
          .print-container { padding: 0 !important; max-width: 100% !important; }
          .print-page-break { page-break-before: always; }
          .print-avoid-break { page-break-inside: avoid; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Demo banner */}
      <div className="bg-amber-50 border-b border-amber-200 no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-900">
            <Sparkles className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
            <span>
              <strong>Demonstration Data</strong> — Not actual employee activity. Sample metrics only.
            </span>
          </div>
          <Link
            to="/callachap"
            data-testid="employer-back-to-landing"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-amber-900 hover:text-amber-950 font-medium"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.2} /> Back to CALLACHAP
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 print-container">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-8 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src="/callachap-logo.png" alt="CALLACHAP" className="h-9 md:h-10 w-auto" />
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-800 no-print">
                Demonstration
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">
              Employer Utilization Dashboard
            </p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              BALM — Demonstration
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Date range: <span className="font-medium text-slate-800">This Quarter</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-3 no-print">
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="employer-download-csv"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={2.2} /> Download Sample Utilization Report
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="employer-print-summary"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-800 hover:border-[#4A6FA5] hover:text-[#4A6FA5] text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              <Printer className="w-4 h-4" strokeWidth={2.2} /> Download Sample Summary
            </button>
          </div>
        </header>

        <p className="print-only text-xs text-slate-600 mt-4">
          Sample Demonstration Data — Not actual employee activity.
        </p>

        {/* Main metric */}
        <section className="mt-10 grid lg:grid-cols-3 gap-5 print-avoid-break">
          <div className="lg:col-span-1">
            <MetricTile
              big
              value={METRICS.employees}
              label="Employees Supported"
              sub="Unique employees who used CALLACHAP"
              icon={Heart}
              testId="metric-employees"
            />
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricTile value={METRICS.connections} label="Support Connections" icon={Users} testId="metric-connections" sub={`Sum: ${methodTotal}`} />
            <MetricTile value={METRICS.phone} label="Phone" icon={Phone} testId="metric-phone" />
            <MetricTile value={METRICS.text} label="Text" icon={MessageSquareText} testId="metric-text" />
            <MetricTile value={METRICS.video} label="Video" icon={Video} testId="metric-video" />
            <MetricTile value={METRICS.message} label="Message" icon={Mail} testId="metric-message" />
            <div className="col-span-2 sm:col-span-3 rounded-2xl p-5 md:p-6 bg-[#F0F4F8] border border-[#4A6FA5]/20">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4 text-[#4A6FA5]" strokeWidth={2.2} aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B597B] font-semibold">
                  Confidential Employee Details Shared With Employer
                </p>
              </div>
              <div className="font-heading text-4xl md:text-5xl text-[#3B597B]">{METRICS.shared}</div>
              <p className="mt-1 text-xs text-[#3B597B]/80">
                CALLACHAP separates confidential employee support from employer reporting.
              </p>
            </div>
          </div>
        </section>

        <p className="mt-3 text-xs text-slate-500">
          One employee may have multiple support connections. Repeated contacts are not counted as additional unique employees.
        </p>

        {/* Chart */}
        <section className="mt-10 rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm print-avoid-break">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Trends
              </p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight text-slate-900">
                Usage over time
              </h2>
              <p className="mt-1 text-xs text-slate-500">Sample Demonstration Data</p>
            </div>
          </div>
          <div className="h-[280px]" data-testid="employer-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={MONTHLY} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="employees"
                  name="Employees Supported"
                  stroke="#4A6FA5"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="connections"
                  name="Support Connections"
                  stroke="#F59E0B"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Privacy panel */}
        <section className="mt-10 grid md:grid-cols-2 gap-5 print-avoid-break">
          <div className="rounded-2xl p-6 md:p-8 bg-white border border-emerald-200">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-emerald-600" strokeWidth={2} aria-hidden="true" />
              <h3 className="font-heading text-lg md:text-xl tracking-tight text-slate-900">
                What the organization can see
              </h3>
            </div>
            <ul className="space-y-2 text-sm text-slate-700" data-testid="employer-can-see">
              {CAN_SEE.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl p-6 md:p-8 bg-white border border-rose-200">
            <div className="flex items-center gap-2 mb-4">
              <EyeOff className="w-5 h-5 text-rose-600" strokeWidth={2} aria-hidden="true" />
              <h3 className="font-heading text-lg md:text-xl tracking-tight text-slate-900">
                What the organization cannot see
              </h3>
            </div>
            <ul className="space-y-2 text-sm text-slate-700" data-testid="employer-cannot-see">
              {CANNOT_SEE.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-rose-500" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Suppressed for privacy */}
        <section className="mt-10 rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm print-avoid-break" data-testid="employer-suppressed-card">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-[#4A6FA5]" strokeWidth={2.2} aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B597B] font-semibold">
                  Privacy threshold
                </p>
              </div>
              <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
                Some detailed utilization categories are hidden when participation is too low.
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                When only a small number of employees would appear in a category, CALLACHAP
                automatically suppresses that detail so individuals cannot be identified from
                aggregate reporting.
              </p>
            </div>
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Detailed topic information
              </p>
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-300 text-xs uppercase tracking-wider font-semibold text-slate-500">
                <Lock className="w-3.5 h-3.5" strokeWidth={2.2} aria-hidden="true" /> Suppressed for privacy
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <p className="mt-10 text-xs text-slate-500 max-w-3xl leading-relaxed">
          CALLACHAP separates confidential employee chaplain support from employer-facing program
          reporting. Employers may see appropriate aggregate utilization information but do not
          receive employee names, contact information, individual reasons for seeking support,
          messages, conversation notes, or chaplain conversation content. CALLACHAP is non-clinical
          chaplain support and does not replace emergency services, medical care, mental-health
          treatment, or an Employee Assistance Program.
        </p>
      </div>
    </main>
  );
}
